import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';


import { makeStyles } from 'tss-react/mui';
import { TextFieldForm, Typography } from 'web-library';
import { InputAdornment } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { ExamFeeModelResponse } from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import { SpersExamFeeFormData } from './schema/SpersExamFeeSchema';
import { fallbackNullNumber, getNumberFromString } from '../../../../utils/NumberUtils';

const useStyles = makeStyles()((theme) =>
({
  input: {
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    paddingBottom: theme.spacing(3),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
  halfWidthLeftItem: {
    width: '50%',
    display: 'inline-flex',
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  halfWidthRightItem: {
    width: '50%',
    display: 'inline-flex',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
    },
  },
}));

interface TimerFeeFormSectionProps {
  title: string;
  formHook: UseFormReturn<SpersExamFeeFormData>;
  isModalOpened: boolean;
  defaultData?: ExamFeeModelResponse;
}

const TimerFeeFormSection = ({
  title,
  formHook,
  isModalOpened,
  defaultData,
}: TimerFeeFormSectionProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { formState: { errors } } = formHook;
  const [firstTimerRegistrationFee, setFirstTimerRegistrationFee] = useState<number | null>(null);
  const [secondTimerRegistrationFee, setSecondTimerRegistrationFee] = useState<number | null>(null);
  const [firstTimerLateFee, setFirstTimerLateFee] = useState<number | null>(null);
  const [secondTimerLateFee, setSecondTimerLateFee] = useState<number | null>(null);

  useEffect(() => {
    if (isModalOpened) {
      // To reset the default value of the state every time modal is opened.
      if (defaultData) {
        setFirstTimerRegistrationFee(fallbackNullNumber(defaultData.firstTimerRegistrationFee));
        setSecondTimerRegistrationFee(fallbackNullNumber(defaultData.secondTimerRegistrationFee));
        setFirstTimerLateFee(fallbackNullNumber(defaultData.firstTimerLateFee));
        setSecondTimerLateFee(fallbackNullNumber(defaultData.secondTimerLateFee));
      } else {
        setFirstTimerRegistrationFee(null);
        setSecondTimerRegistrationFee(null);
        setFirstTimerLateFee(null);
        setSecondTimerLateFee(null);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.title}>
        {t('exam->>ExamFeeConfiguration.LateFeeInstruction')}
      </Typography>
      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.FirstTimerRegistrationFee')}
        name="firstTimerRegistrationFee"
        placeholder="0.00"
        type="number"
        error={!!errors.firstTimerRegistrationFee}
        helperText={errors?.firstTimerRegistrationFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={`${classes.input} ${classes.halfWidthLeftItem}`}
        value={firstTimerRegistrationFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setFirstTimerRegistrationFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />
      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.SecondTimerRegistrationFee')}
        name="secondTimerRegistrationFee"
        placeholder="0.00"
        type="number"
        error={!!errors.secondTimerRegistrationFee}
        helperText={errors?.secondTimerRegistrationFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={`${classes.input} ${classes.halfWidthRightItem}`}
        value={secondTimerRegistrationFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setSecondTimerRegistrationFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />

      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.FirstTimerLateFee')}
        name="firstTimerLateFee"
        placeholder="0.00"
        type="number"
        error={!!errors.firstTimerLateFee}
        helperText={errors?.firstTimerLateFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={`${classes.input} ${classes.halfWidthLeftItem}`}
        value={firstTimerLateFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setFirstTimerLateFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />
      <TextFieldForm
        form={formHook}
        label={t('exam->>ExamFeeConfiguration.SecondTimerLateFee')}
        name="secondTimerLateFee"
        placeholder="0.00"
        type="number"
        error={!!errors.secondTimerLateFee}
        helperText={errors?.secondTimerLateFee?.message?.toString()}
        inputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fullWidth
        isRequired
        className={`${classes.input} ${classes.halfWidthRightItem}`}
        value={secondTimerLateFee}
        // To limit user input to 2 decimal place
        onChange={(e) => {
          const val = getNumberFromString(e.target.value, 2);
          setSecondTimerLateFee(val);
        }}
        onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
      />
    </>
  );
};

export default TimerFeeFormSection;
